import {
  ListItemButton,
  ListItemText,
  ListItem,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import Asset from "datalign-module-template/models/asset";
import { MaterialIcon } from "ui-widgets";
import { GetFileExtension } from "datalign-module-template/helpers/GetFileExtension";
import {
  TEXT_EXTENSIONS,
  CODE_EXTENSIONS,
  AUDIO_EXTENSIONS,
  VIDEO_EXTENSIONS,
  IMAGE_EXTENSIONS,
} from "datalign-module-template/components/extensionsList";
import DownloadAsset from "datalign-module-template/helpers/DownloadAsset";
import AssetPreviewModal from "components/common/asset-preview-button/asset-preview-modal";

export default function FileMenu({
  asset,
  last,
}: {
  asset: Asset;
  last: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openModal, setOpenModal] = useState(false);
  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const [loaded, setLoaded] = useState(false);
  const [fileContent, setFileContent] = useState<
    string | ArrayBuffer | object | null
  >(null);

  const extension = useMemo(() => GetFileExtension(asset.name), [asset]);

  const icon = useMemo(() => {
    switch (extension) {
      case ".pdf":
        return "picture_as_pdf";
      case ".csv":
        return "csv";
      case ".gif":
        return "gif_box";
      default:
        break;
    }

    if ([".hevc", ".h265", ".265"].includes(extension)) {
      return "hevc";
    }
    if (
      [
        ".tar",
        ".zip",
        ".7zip",
        ".zipx",
        ".rar",
        ".gz",
        ".tgz",
        ".tar.gz",
        ".bz2",
        ".tar.bz2",
        ".tgz",
        ".apk",
        ".ipa",
        ".jar",
        ".cab",
      ].includes(extension)
    ) {
      return "folder_zip";
    }

    if ([".json", ".geojson"].includes(extension)) {
      return "file_json";
    }

    if ([".htm", ".html"].includes(extension)) {
      return "html";
    }

    if (
      [
        ".js",
        ".ts",
        ".tsx",
        ".vue",
        ".vuex",
        ".mjs",
        ".d.ts",
        ".spec.ts",
      ].includes(extension)
    ) {
      return "javascript";
    }

    if ([".css", ".scss", ".less"].includes(extension)) {
      return "css";
    }

    if (
      [".phtml", ".php3", ".php4", ".php5", ".php", ".inc"].includes(extension)
    ) {
      return "php";
    }

    if (
      [".bash", ".sh", ".py", ".pl", ".rb", ".java", ".go"].includes(extension)
    ) {
      return "terminal";
    }

    if (VIDEO_EXTENSIONS.includes(extension)) {
      return "video_file";
    }
    if (AUDIO_EXTENSIONS.includes(extension)) {
      return "audio_file";
    }
    if (IMAGE_EXTENSIONS.includes(extension)) {
      return "photo_library";
    }
    if (TEXT_EXTENSIONS.includes(extension)) {
      return "docs";
    }
    if (CODE_EXTENSIONS.includes(extension)) {
      return "code_blocks";
    }
    return "file_present";
  }, [extension]);

  const handlePreview = async () => {
    setOpenModal(true);
    setFileContent("Empty file content.");
    try {
      setLoaded(false);
      const response = await fetch(asset.presigned_url, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch the file.");
      }

      if (extension === ".pdf") {
        const arrayBuffer = await response.arrayBuffer();
        setFileContent(arrayBuffer);
      } else if (extension === ".csv") {
        const textContent = await response.text();
        setFileContent(textContent);
      } else if ([".json", ".geojson"].includes(extension)) {
        const jsonObject = await response.json();
        setFileContent(jsonObject);
      } else if (TEXT_EXTENSIONS.includes(extension)) {
        const textContent = await response.text();
        setFileContent(textContent);
      } else if (CODE_EXTENSIONS.includes(extension)) {
        const textContent = await response.text();
        setFileContent(textContent);
      } else {
        console.log("Unknown file type");
        setFileContent("Unsupported file type");
      }
    } catch (error) {
      console.error("Error loading file:", error);
      setFileContent("Error loading file.");
    } finally {
      setLoaded(true);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div key={`file-${asset.guid}`}>
      <ListItem>
        <ListItemButton onClick={handleClickListItem}>
          <ListItemIcon>
            <MaterialIcon icon={icon} />
          </ListItemIcon>
          <ListItemText primary={asset.name} />
        </ListItemButton>
      </ListItem>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ list: { role: "listbox" } }}
      >
        <MenuItem
          onClick={() => {
            DownloadAsset(asset);
            setAnchorEl(null);
          }}
        >
          <MaterialIcon icon="download" />
          Download
        </MenuItem>
        <MenuItem onClick={handlePreview}>
          <MaterialIcon icon="preview" />
          Preview
        </MenuItem>
      </Menu>
      <AssetPreviewModal
        asset={asset}
        open={openModal}
        onClose={() => setOpenModal(false)}
        fileContent={fileContent}
        loaded={loaded}
        extension={extension}
      />
      {!last && <Divider />}
    </div>
  );
}
